<template>
  <documents-metadata
    ref="documentMetadataRef"
    :fetch-metadata-and-field-infos="getWorkspaceDocumentMetadata"
    :update-metadata-function="enqueueMetadataUpdate"
    :can-edit-metadata="canEditMetadata"
    :save-invalid="false"
    :busy-fields="busyFields"
  >
    <template #value-KKS="{ data }">
      <div class="kks-selector-container">
        <kks-selector
          :key="kksPickerRerenderKey"
          v-model="data.value"
          class="kks-selector"
          :busy="isLoading"
          :can-remove-last="false"
          :disabled="!canEditMetadata"
          :label="t('documentDetailView.kksTree')"
          :location="workspaceLocation!"
          :workspace-id="canManageWorkspaceDocuments ? workspaceId : undefined"
          @change="updateKks"
        />
      </div>
    </template>
  </documents-metadata>
</template>

<script setup lang="ts">
  import { useGetWorkspace } from '@/composables/api/workspaces/useGetWorkspace';
  import { useToast } from '@/composables/useToast';
  import { endpointsList } from '@/helpers';
  import axios from 'axios';
  import {
    DEFAULT_METADATA_VALUES_SEPARATOR,
    IUpdateWorkspaceDocumentMetadataDto,
    IWorkspaceDocumentMetadataResponseDto,
    KKS_METADATA_FIELD,
  } from 'types';
  import { computed, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { KksSelectorChangeEvent } from '../TreeSelect/KksSelector.types';
  import DocumentsMetadata from './DocumentsMetadata.vue';
  import { DocumentsMetadataResponse } from './DocumentsMetadata.types';
  import { useRoles } from '@/composables/useRoles';

  const props = defineProps<{
    workspaceId: string;
    documentId: string;
    canEditMetadata: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'document-data-changed'): void;
    (e: 'metadata-loaded', metadata: DocumentsMetadataResponse): void;
  }>();

  const { t } = useI18n();
  const { createErrorToast } = useToast();
  const { workspace, isLoadingWorkspace } = useGetWorkspace(props.workspaceId);
  const { canManageWorkspaceDocuments } = useRoles();

  const kksPickerRerenderKey = ref(0);
  const isLoadingMetadata = ref(false);
  const documentMetadataRef = ref<InstanceType<
    typeof DocumentsMetadata
  > | null>(null);

  const workspaceLocation = computed(() => workspace.value?.location ?? null);
  const isLoading = computed(() => {
    return isLoadingMetadata.value || isLoadingWorkspace.value;
  });

  async function getWorkspaceDocumentMetadata(): Promise<DocumentsMetadataResponse> {
    try {
      const response = await axios.get<IWorkspaceDocumentMetadataResponseDto>(
        endpointsList.workspaceUploads.getWorkspaceDocumentMetadata(
          props.workspaceId,
          props.documentId,
        ),
      );
      emit('metadata-loaded', response.data);
      return response.data;
    } catch (error) {
      createErrorToast(t('errors.error'));
      throw error;
    }
  }

  const enqueuedMetadataUpdates = ref<Record<string, string | string[]>>({});
  const metadataUpdatesInProgress = ref<Record<string, string | string[]>>({});
  const isUpdatingMetadata = ref(false);
  const busyFields = computed<string[]>(() => [
    ...Object.keys(enqueuedMetadataUpdates.value),
    ...Object.keys(metadataUpdatesInProgress.value),
  ]);

  async function enqueueMetadataUpdate(
    metadataField: string,
    fieldValue: string | string[],
  ): Promise<void> {
    enqueuedMetadataUpdates.value[metadataField] = fieldValue;
  }

  watch(
    () => [enqueuedMetadataUpdates.value, isUpdatingMetadata.value],
    () => {
      if (
        Object.keys(enqueuedMetadataUpdates.value).length > 0 &&
        !isUpdatingMetadata.value
      ) {
        updateWorkspaceDocumentMetadata();
      }
    },
    { deep: true },
  );

  async function updateWorkspaceDocumentMetadata(): Promise<void> {
    isUpdatingMetadata.value = true;
    const payload = getMetadataUpdateDtoAndClearQueue();
    try {
      await axios.patch(
        endpointsList.workspaceUploads.updateWorkspaceDocumentMetadata(
          props.workspaceId,
          props.documentId,
        ),
        payload,
      );
      await documentMetadataRef.value?.getMetadataAndFieldInfos();
      emit('document-data-changed');
    } catch (error) {
      createErrorToast(t('errors.error'));
      throw error;
    } finally {
      metadataUpdatesInProgress.value = {};
      isUpdatingMetadata.value = false;
    }
  }

  function getMetadataUpdateDtoAndClearQueue(): IUpdateWorkspaceDocumentMetadataDto {
    const dto: IUpdateWorkspaceDocumentMetadataDto = {};
    for (const [metadataField, fieldValue] of Object.entries(
      enqueuedMetadataUpdates.value,
    )) {
      dto[metadataField] = Array.isArray(fieldValue)
        ? fieldValue.join(DEFAULT_METADATA_VALUES_SEPARATOR)
        : fieldValue;
    }
    metadataUpdatesInProgress.value = {
      ...enqueuedMetadataUpdates.value,
    };
    enqueuedMetadataUpdates.value = {};
    return dto;
  }

  async function updateKks(e: KksSelectorChangeEvent): Promise<void> {
    try {
      isLoadingMetadata.value = true;
      const kks = e.target.value.map((item) => item.kks_trimmed);

      await enqueueMetadataUpdate(KKS_METADATA_FIELD, kks);
      await documentMetadataRef.value?.getMetadataAndFieldInfos();
    } catch {
      return;
    } finally {
      isLoadingMetadata.value = false;
    }
  }
</script>

<style scoped lang="scss">
  .kks-selector-container {
    display: flex;
    align-items: center;
    column-gap: 1rem;
  }
</style>
